/**
 * JRB11 Press Logo Bar — mobile Swiper / desktop crossfade + autoplay.
 * Expects window.Swiper from theme (assets/swiper7.4.1.min.js in layout/theme.liquid).
 */
(() => {
    const MOBILE_BREAKPOINT = 768;
    const AUTOPLAY_DELAY = 5000;
    const INIT_ATTR = 'data-jrb11-initialized';

    const selectors = {
      section: '.jrb11-v1',
      container: '.jrb11-quotes-container',
      wrapper: '.swiper-wrapper',
      slide: '.jrb11-quote-slide',
      logo: '.jrb11-logo-nav',
      progress: '.jrb11-progress-fill',
      logosWrapper: '.jrb11-desktop-logos-wrapper',
    };

    class ElementObserver {
      constructor(config) {
        this.observer = new MutationObserver(() => this.process());
        this.observer.observe(document, { childList: true, subtree: true });
        this.config = Array.isArray(config) ? config : [config];
        for (const item of this.config) {
          item._lookup = new Map();
        }
        this.process();
      }

      process() {
        const pathInfo = this.getPathInfo();
        for (const cfg of this.config) {
          this.processOne(pathInfo, cfg);
        }
      }

      processOne(pathInfo, cfg) {
        const elements = cfg.getElements(pathInfo);
        const set = this.getElementSet(elements);

        for (const el of set) {
          if (!cfg._lookup.has(el)) {
            const data = {};
            cfg.onNewElement?.(el, data, this.observer);
            if (cfg.onChangeElement) {
              const obs = new MutationObserver(() => cfg.onChangeElement(el, data, obs));
              obs.observe(el, { childList: true, subtree: true, attributes: true });
              cfg._lookup.set(el, { element: el, observer: obs });
            } else {
              cfg._lookup.set(el, { element: el });
            }
          }
        }

        for (const state of cfg._lookup.values()) {
          if (!set.has(state.element)) {
            state.observer?.disconnect();
            cfg.onRemoveElement?.(state.element);
            cfg._lookup.delete(state.element);
          }
        }
      }

      getPathInfo() {
        const path = location.pathname;
        if (path === '/') return { path, type: 'index' };
        if (path.indexOf('/products/') > -1) return { path, type: 'pdp' };
        if (path.startsWith('/collections')) return { path, type: 'collection' };
        if (path.startsWith('/pages/')) return { path, type: 'page' };
        return { path, type: undefined };
      }

      getElementSet(obj) {
        if (obj == null) return new Set();
        return typeof obj[Symbol.iterator] === 'function' ? new Set(obj) : new Set([obj]);
      }
    }

    let instances = new Map();
    let swiperWaitPromise = null;
    let resizeTimeout = null;

    /** Resolves once theme-loaded Swiper is on window (no CDN fallback). */
    function ensureSwiperAssets() {
      if (window.Swiper) return Promise.resolve();
      if (swiperWaitPromise) return swiperWaitPromise;

      swiperWaitPromise = new Promise((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 150;
        function tick() {
          if (window.Swiper) {
            swiperWaitPromise = null;
            resolve();
            return;
          }
          attempts += 1;
          if (attempts >= maxAttempts) {
            swiperWaitPromise = null;
            console.error('JRB11: timed out waiting for theme Swiper (swiper7.4.1.min.js)');
            reject(new Error('Swiper not loaded'));
            return;
          }
          requestAnimationFrame(tick);
        }
        tick();
      });

      return swiperWaitPromise;
    }

    function attachLogoHandlers(data) {
      if (!data.logoNavs.length) return;
      data.logoNavs.forEach((nav, idx) => {
        const handler = () => {
          if (window.innerWidth < MOBILE_BREAKPOINT) {
            if (data.swiper) data.swiper.slideToLoop(idx);
          } else {
            stopDesktopAutoplay(data);
            showDesktopSlide(data, idx);
            startDesktopAutoplay(data);
          }
        };
        nav.addEventListener('click', handler);
        if (!data.logoHandlers) data.logoHandlers = [];
        data.logoHandlers.push({ nav, handler });
      });
    }

    function updateProgress(data, index) {
      if (!data.progressFill || !data.slides.length) return;
      const segment = 100 / data.slides.length;
      data.progressFill.style.width = `${segment}%`;
      data.progressFill.style.marginLeft = `${segment * index}%`;
    }

    function centerDesktopLogo(data, index) {
      if (!data.logosWrapper || window.innerWidth < MOBILE_BREAKPOINT) return;
      const activeLogo = data.logoNavs[index];
      if (!activeLogo) return;

      const wrapperWidth = data.logosWrapper.offsetWidth;
      const logoLeft = activeLogo.offsetLeft;
      const logoWidth = activeLogo.offsetWidth;
      const scrollPosition = logoLeft - wrapperWidth / 2 + logoWidth / 2;
      const maxScrollLeft = data.logosWrapper.scrollWidth - wrapperWidth;
      const targetScroll = Math.max(0, Math.min(scrollPosition, maxScrollLeft));

      data.logosWrapper.scrollTo({ left: targetScroll, behavior: 'smooth' });
    }

    function showDesktopSlide(data, index) {
      const total = data.slides.length;
      if (!total) return;
      const newIndex = ((index % total) + total) % total;
      data.currentIndex = newIndex;

      data.slides.forEach((slide) => slide.classList.remove('active'));
      data.logoNavs.forEach((nav) => nav.classList.remove('active'));

      const activeSlide = data.slides[newIndex];
      if (activeSlide) activeSlide.classList.add('active');
      const activeLogo = data.logoNavs[newIndex];
      if (activeLogo) activeLogo.classList.add('active');

      updateProgress(data, newIndex);
      centerDesktopLogo(data, newIndex);
    }

    function stopDesktopAutoplay(data) {
      if (data.desktopTimer) {
        clearInterval(data.desktopTimer);
        data.desktopTimer = null;
      }
    }

    function startDesktopAutoplay(data) {
      if (data.slides.length <= 1) return;
      stopDesktopAutoplay(data);
      data.desktopTimer = setInterval(() => {
        showDesktopSlide(data, data.currentIndex + 1);
      }, AUTOPLAY_DELAY);
    }

    function destroyMobile(data) {
      if (data.swiper) {
        const idx = data.swiper.realIndex || 0;
        data.swiper.destroy(true, true);
        data.swiper = null;
        data.currentIndex = idx;
      }
      data.mobileInitPromise = null;
    }

    function enableDesktop(data) {
      if (data.mode === 'desktop') return;
      destroyMobile(data);
      if (data.currentIndex >= data.slides.length) data.currentIndex = 0;
      showDesktopSlide(data, data.currentIndex);
      startDesktopAutoplay(data);
      data.mode = 'desktop';
    }

    function enableMobile(data) {
      if (data.swiper) {
        data.mode = 'mobile';
        if (data.swiper.autoplay) data.swiper.autoplay.start();
        updateProgress(data, data.swiper.realIndex || 0);
        return;
      }

      if (data.mobileInitPromise) return;

      stopDesktopAutoplay(data);
      data.slides.forEach((slide) => slide.classList.remove('active'));
      data.logoNavs.forEach((nav) => nav.classList.remove('active'));

      data.mobileInitPromise = ensureSwiperAssets()
        .then(() => {
          data.mobileInitPromise = null;
          if (window.innerWidth >= MOBILE_BREAKPOINT || data.swiper) return;

          data.swiper = new window.Swiper(data.container, {
            loop: true,
            slidesPerView: 1,
            allowTouchMove: true,
            autoplay: {
              delay: AUTOPLAY_DELAY,
              disableOnInteraction: false,
            },
            on: {
              init: (swiper) => {
                const idx = swiper.realIndex || 0;
                data.currentIndex = idx;
                updateProgress(data, idx);
              },
              slideChange: (swiper) => {
                const idx = swiper.realIndex || 0;
                data.currentIndex = idx;
                updateProgress(data, idx);
              },
            },
          });

          if (data.currentIndex > 0) {
            data.swiper.slideToLoop(data.currentIndex, 0);
          }
          data.mode = 'mobile';
        })
        .catch((error) => {
          console.error('JRB11: mobile initialisation failed', error);
          data.mobileInitPromise = null;
        });
    }

    function updateMode(data) {
      if (!data.section.isConnected) return;
      if (window.innerWidth < MOBILE_BREAKPOINT) {
        enableMobile(data);
      } else {
        enableDesktop(data);
      }
    }

    function createInstance(section) {
      const container = section.querySelector(selectors.container);
      if (!container) return null;
      const wrapper = container.querySelector(selectors.wrapper);
      if (!wrapper) return null;
      const slides = Array.from(wrapper.querySelectorAll(selectors.slide));
      if (!slides.length) return null;
      const logoNavs = Array.from(section.querySelectorAll(selectors.logo));
      const progressFill = section.querySelector(selectors.progress);
      const logosWrapper = section.querySelector(selectors.logosWrapper);

      slides.forEach((slide, idx) => {
        slide.dataset.slideIndex = idx;
        if (!slide.classList.contains('swiper-slide')) {
          slide.classList.add('swiper-slide');
        }
      });
      if (!wrapper.classList.contains('swiper-wrapper')) {
        wrapper.classList.add('swiper-wrapper');
      }

      const data = {
        section,
        container,
        wrapper,
        slides,
        logoNavs,
        progressFill,
        logosWrapper,
        swiper: null,
        desktopTimer: null,
        currentIndex: 0,
        mode: null,
        mobileInitPromise: null,
        logoHandlers: [],
        resizeHandler: null,
        visibilityHandler: null,
      };

      attachLogoHandlers(data);
      updateProgress(data, 0);
      updateMode(data);

      // Attach resize handler
      data.resizeHandler = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => updateMode(data), 150);
      };
      window.addEventListener('resize', data.resizeHandler);

      // Attach visibility handler
      data.visibilityHandler = () => {
        if (document.hidden) {
          stopDesktopAutoplay(data);
          if (data.swiper && data.swiper.autoplay) data.swiper.autoplay.stop();
        } else {
          updateMode(data);
        }
      };
      document.addEventListener('visibilitychange', data.visibilityHandler);

      return data;
    }

    function destroyInstance(section) {
      const data = instances.get(section);
      if (!data) return;

      stopDesktopAutoplay(data);
      destroyMobile(data);

      // Remove event listeners
      if (data.logoHandlers) {
        data.logoHandlers.forEach(({ nav, handler }) => {
          nav.removeEventListener('click', handler);
        });
      }
      if (data.resizeHandler) {
        window.removeEventListener('resize', data.resizeHandler);
      }
      if (data.visibilityHandler) {
        document.removeEventListener('visibilitychange', data.visibilityHandler);
      }

      data.section.removeAttribute(INIT_ATTR);
      instances.delete(section);
    }

    // Initialize with ElementObserver
    new ElementObserver({
      getElements: () => {
        return document.querySelectorAll(selectors.section);
      },
      onNewElement: (section) => {
        if (section.hasAttribute(INIT_ATTR)) return;
        const data = createInstance(section);
        if (!data) return;
        section.setAttribute(INIT_ATTR, 'true');
        instances.set(section, data);
      },
      onRemoveElement: (section) => {
        destroyInstance(section);
      },
    });

    // Handle popstate (back/forward navigation) — clean up disconnected instances
    window.addEventListener('popstate', () => {
      Array.from(instances.keys()).forEach((section) => {
        if (!section.isConnected) destroyInstance(section);
      });
    });
  })();
